import type { IAttachment, ISavedDocument } from "findef";
import { useFindexSocket } from "../useFindexSocket";
import { EventSystem } from "@/utils/event/eventSystem";

export enum EFileTransactionStatus {
  PENDING = 'PENDING',
  LOADING = 'LOADING',
  OK = 'OK',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED'
}

export type UseFileTransactionStartParams = {
  parentDirectoryId?: string;
  public?: boolean;
}

export type UseFileTransactionProps = {
  file: File;
  name?: string;
  mime?: string;
  startParams?: UseFileTransactionStartParams;
}

export type UseFileTransaction = {
  start: () => Promise<ISavedDocument<IAttachment> | null>;
  cancel: () => void;
  file: File;
  name: string;
  progress: ComputedRef<number>;
  bytesSent: Ref<number>;
  //isCanceled: Ref<boolean>;
  isLoading: Ref<boolean>;
  isFailed: ComputedRef<boolean>;
  isCanceled: ComputedRef<boolean>;
  isOK: ComputedRef<boolean>;
  status: Ref<EFileTransactionStatus>;
  attachment: Ref<ISavedDocument<IAttachment> | null>;
  events: EventSystem<UseFileTransactionEventMap>;
  isSpoofed?: boolean;
  originalId?: string;
  __v_skip: true;
};

export type UseFileTransactionEventMap = {
  [EFileTransactionStatus.PENDING]: { eventType: EFileTransactionStatus.PENDING, name: string },
  [EFileTransactionStatus.LOADING]: { eventType: EFileTransactionStatus.LOADING, name: string },
  [EFileTransactionStatus.OK]: { eventType: EFileTransactionStatus.OK, name: string, attachment: ISavedDocument<IAttachment> },
  [EFileTransactionStatus.FAILED]: { eventType: EFileTransactionStatus.FAILED, name: string },
  [EFileTransactionStatus.CANCELED]: { eventType: EFileTransactionStatus.CANCELED, name: string },
}

export const useFileTransaction = (props: UseFileTransactionProps): UseFileTransaction => {
  const socket = useFindexSocket();
  const events = new EventSystem<UseFileTransactionEventMap>();
  //const isCanceled = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const status = ref<EFileTransactionStatus>(EFileTransactionStatus.PENDING);
  const bytesSent = ref<number>(0);
  const totalSize = props.file.size || 1;
  const progress = computed(() => bytesSent.value / Math.max(1.0, totalSize));
  const attachment = ref<ISavedDocument<IAttachment> | null>(null);
  const isFailed = computed(() => status.value === EFileTransactionStatus.FAILED);
  const isCanceled = computed(() => status.value === EFileTransactionStatus.CANCELED);
  const isOK = computed(() => status.value === EFileTransactionStatus.OK);
  let userDidCancel: boolean = false;

  const name = props.name || props.file.name;
  const mime = props.mime || props.file.type || 'unknown';
  //let cleanup: (() => void) = () => {};

  const cancel = () => {
    userDidCancel = true;
    if (status.value === EFileTransactionStatus.CANCELED) return;

    try {
      socket.cancelTransaction(name);
    } catch (e) {
      console.error(e);
    }

    attachment.value = null;
    isLoading.value = false;
    status.value = EFileTransactionStatus.CANCELED;
  }


  const start = async (): Promise<ISavedDocument<IAttachment> | null> => {
    if (status.value === EFileTransactionStatus.LOADING) return null;
    //isCanceled.value = false;
    isLoading.value = true;
    userDidCancel = false;
    status.value = EFileTransactionStatus.LOADING;
    events.emit({ eventType:  EFileTransactionStatus.LOADING, name });

    try {

      const resp = await socket.transfer({
        data: props.file,
        name: name,
        app: 'fileUpload',
        startParams: {
          mime: mime,
          ...(props.startParams || {}),
        },
        onProgress: (_socket, nBytes) => {
          bytesSent.value = nBytes;
        },
        onFinish: (_socket, ok) => {
          isLoading.value = false;
          if (ok) {
            status.value = EFileTransactionStatus.OK;
          } else {
            status.value = userDidCancel ? EFileTransactionStatus.CANCELED : EFileTransactionStatus.FAILED;
          }
        }
      });

      if (!resp || !resp.ok) {
        status.value = userDidCancel ? EFileTransactionStatus.CANCELED :  EFileTransactionStatus.FAILED;
        events.emit({ eventType: userDidCancel ? EFileTransactionStatus.CANCELED :  EFileTransactionStatus.FAILED, name });
        isLoading.value = false;
        return null;
      }
      status.value = EFileTransactionStatus.OK;
      const attach = resp.attachment as ISavedDocument<IAttachment> | undefined;
      if (!attach) {
        status.value = userDidCancel ? EFileTransactionStatus.CANCELED :  EFileTransactionStatus.FAILED;
        events.emit({ eventType: userDidCancel ? EFileTransactionStatus.CANCELED :  EFileTransactionStatus.FAILED, name });
        return null;
      }

      attachment.value = attach;

      if (attach) {
        events.emit({ eventType: EFileTransactionStatus.OK, attachment: attach, name: name });
      }
      return attach;
    } catch (e) {
      console.error(e);
      isLoading.value = false;
      status.value = userDidCancel ? EFileTransactionStatus.CANCELED :  EFileTransactionStatus.FAILED;
      events.emit({ eventType: userDidCancel ? EFileTransactionStatus.CANCELED :  EFileTransactionStatus.FAILED, name });
      return null;
    }
  }

  return {
    start,
    cancel,
    progress,
    isLoading,
    isFailed,
    isCanceled,
    isOK,
    status,
    bytesSent,
    attachment,
    events,
    file: props.file,
    name: name,
    __v_skip: true
  }
}
